import React, { useState, useRef, useEffect } from 'react';
import PropTypes from 'prop-types';
import legacyImage from '../images/legacy_healing_center_logo.svg';
import { useAuth } from '../AuthContext';
import { useNavigate } from 'react-router-dom';

const LegacyHeader = ({ color = '#1F3047', fontSize = 30 }) => {
  const { isAuthenticated, logout, currentUser } = useAuth();
  const [showPopup, setShowPopup] = useState(false);
  const popupRef = useRef(null);
  const navigate = useNavigate();
  // Close popup when clicking outside
  useEffect(() => {
    const handleClickOutside = (event) => {
      if (popupRef.current && !popupRef.current.contains(event.target)) {
        setShowPopup(false);
      }
    };

    document.addEventListener('mousedown', handleClickOutside);
    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
    };
  }, []);

  const handleLogout = () => {
    logout();
    navigate('/login');
  };

  const togglePopup = () => {
    setShowPopup(!showPopup);
  };

  return (
    <div style={headerContainerStyle}>
      <div style={logoContainerStyle}>
        <img 
          src={legacyImage} 
          alt="Legacy Healing Center Logo" 
          style={logoStyle} 
        />
      </div>
      <h1 style={{
        ...titleStyle,
        color: `var(--Primary-Color, ${color})`,
        fontSize: `clamp(24px, 5vw, ${fontSize}px)`
      }}>
        Legacy Healing Digital Intake
      </h1>

      {isAuthenticated && (
        <div style={userMenuContainerStyle} ref={popupRef}>
          <div 
            style={userInfoStyle}
            onClick={togglePopup}
          >
            <span style={userNameStyle}>
              {currentUser?.email || 'User'}
            </span>
            <button style={settingsButtonStyle}>
              ⚙️
            </button>
          </div>
          
          {showPopup && (
            <div style={popupMenuStyle}>
              <div style={popupHeaderStyle}>
                <span style={popupUserNameStyle}>
                  {currentUser?.email || 'User'}
                </span>
              </div>
              <div style={popupDividerStyle}></div>
              <button 
                onClick={handleLogout}
                style={popupItemStyle}
              >
                Log Out
              </button>
            </div>
          )}
        </div>
      )}
    </div>
  );
};

// New styles
const userMenuContainerStyle = {
  position: 'absolute',
  right: '16px',
  top: '50%',
  transform: 'translateY(-50%)',
  display: 'flex',
  flexDirection: 'column',
  alignItems: 'flex-end',
  zIndex: 100,
  cursor: 'pointer',
  '@media (max-width: 768px)': {
    position: 'relative',
    right: 'auto',
    top: 'auto',
    transform: 'none',
    marginTop: '12px',
    order: 3,
    width: '100%',
    alignItems: 'center'
  }
};

const userInfoStyle = {
  display: 'flex',
  alignItems: 'center',
  gap: '8px',
  padding: '4px 8px',
  borderRadius: '4px',
  ':hover': {
    backgroundColor: 'rgba(0,0,0,0.05)'
  }
};

const userNameStyle = {
  color: '#1F3047',
  fontFamily: "'FONTSPRING DEMO - Commuters Sans', sans-serif",
  fontSize: '14px',
  fontWeight: 500
};

const settingsButtonStyle = {
  background: 'none',
  border: 'none',
  fontSize: '20px',
  cursor: 'pointer',
  padding: '4px',
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'center'
};

const popupMenuStyle = {
  position: 'absolute',
  top: '100%',
  right: 0,
  backgroundColor: 'white',
  borderRadius: '8px',
  boxShadow: '0 4px 12px rgba(0,0,0,0.15)',
  width: '200px',
  overflow: 'hidden',
  marginTop: '8px',
  '@media (max-width: 768px)': {
    position: 'relative',
    width: '100%',
    marginTop: '4px',
    boxShadow: 'none',
    border: '1px solid #eee'
  }
};

const popupHeaderStyle = {
  padding: '12px 16px',
  backgroundColor: '#f8f9fa'
};

const popupUserNameStyle = {
  display: 'block',
  fontWeight: '600',
  fontSize: '14px',
  color: '#1F3047',
  marginBottom: '4px'
};

const popupUserEmailStyle = {
  display: 'block',
  fontSize: '12px',
  color: '#666'
};

const popupDividerStyle = {
  height: '1px',
  backgroundColor: '#eee',
  width: '100%'
};

const popupItemStyle = {
  width: '100%',
  padding: '12px 16px',
  textAlign: 'left',
  background: 'none',
  border: 'none',
  cursor: 'pointer',
  fontFamily: "'FONTSPRING DEMO - Commuters Sans', sans-serif",
  fontSize: '14px',
  color: '#1F3047',
  display: 'flex',
  alignItems: 'center',
  ':hover': {
    backgroundColor: 'rgba(52, 154, 200, 0.1)',
    color: '#349AC8'
  }
};

const headerContainerStyle = {
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'center',
  gap: '16px',
  width: '100%',
  position: 'relative',
  padding: '16px',
  boxSizing: 'border-box',
  flexWrap: 'wrap'
};

const logoContainerStyle = {
  position: 'absolute',
  left: '16px',
  top: '50%',
  transform: 'translateY(-50%)',
  '@media (max-width: 1024px)': { // iPad landscape
    left: '12px'
  },
  '@media (max-width: 768px)': { // iPad portrait and larger phones
    position: 'relative',
    left: 'auto',
    top: 'auto',
    transform: 'none',
    textAlign: 'center',
    width: '100%',
    marginBottom: '8px',
    order: 1
  },
  '@media (max-width: 480px)': { // Most phones
    marginBottom: '4px'
  }
};

const logoStyle = {
  height: 'clamp(40px, 8vw, 20px)',
  width: 'auto',
  maxWidth: '100%',
  '@media (max-width: 480px)': {
    height: 'clamp(36px, 8vw, 50px)'
  }
};

const titleStyle = {
  fontFamily: 'Mauren',
  fontWeight: 100,
  wordWrap: 'break-word',
  margin: '0',
  textAlign: 'center',
  flex: '1 1 auto',
  padding: '0 60px',
  '@media (max-width: 1024px)': {
    padding: '0 48px'
  },
  '@media (max-width: 768px)': {
    padding: '0 16px',
    width: '100%',
    order: 2
  },
  '@media (max-width: 480px)': {
    fontSize: 'clamp(20px, 6vw, 22px) !important',
    padding: '0 8px'
  }
};

// PropTypes
LegacyHeader.propTypes = {
  color: PropTypes.string,
  fontSize: PropTypes.number
};

export default LegacyHeader;